// ─────────────────────────────────────────────────────────────────────────────
// LCCA NEXUS — UI Primitives: Card, Input, Badge, Modal, Tooltip
// ─────────────────────────────────────────────────────────────────────────────
import { cn } from '@/lib/utils';
import { X } from 'lucide-react';
import { forwardRef, useEffect } from 'react';
import { Button } from './Button';

// ── Card ──────────────────────────────────────────────────────────────────────

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  noPadding?: boolean;
}

export function Card({ className, children, noPadding, ...props }: CardProps) {
  return (
    <div className={cn('card-base', className)} {...props}>
      {children}
    </div>
  );
}

export function CardHeader({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'flex items-center gap-3 px-5 py-4 border-b border-parchment-200',
        'bg-gradient-to-r from-parchment-50 to-white',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export function CardBody({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('p-5', className)} {...props}>
      {children}
    </div>
  );
}

export function CardFooter({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'flex items-center justify-between gap-3 px-5 py-4',
        'border-t border-parchment-200 bg-parchment-50',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

// ── Input ─────────────────────────────────────────────────────────────────────

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  prefix?: string;
  suffix?: string;
  required?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, hint, prefix, suffix, required, className, id, ...props }, ref) => {
    const inputId = id ?? label?.toLowerCase().replace(/\s+/g, '-');
    return (
      <div className="flex flex-col gap-1.5">
        {label && (
          <label htmlFor={inputId} className="text-xs font-semibold font-body text-navy-700 flex items-center gap-1">
            {label}
            {required && <span className="text-amber-600">*</span>}
          </label>
        )}
        <div className="relative flex items-center">
          {prefix && (
            <span className="absolute left-3 text-sm font-semibold text-navy-400 pointer-events-none select-none">
              {prefix}
            </span>
          )}
          <input
            ref={ref}
            id={inputId}
            className={cn(
              'input-base',
              prefix && 'pl-7',
              suffix && 'pr-12',
              error && 'error',
              className
            )}
            {...props}
          />
          {suffix && (
            <span className="absolute right-3 text-xs text-navy-400 pointer-events-none font-mono select-none">
              {suffix}
            </span>
          )}
        </div>
        {error && <p className="text-xs text-red-600 font-medium">{error}</p>}
        {hint && !error && <p className="text-xs text-navy-400">{hint}</p>}
      </div>
    );
  }
);
Input.displayName = 'Input';

// ── Select ────────────────────────────────────────────────────────────────────

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  options: Array<{ value: string; label: string }>;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, error, options, className, id, ...props }, ref) => {
    const selectId = id ?? label?.toLowerCase().replace(/\s+/g, '-');
    return (
      <div className="flex flex-col gap-1.5">
        {label && (
          <label htmlFor={selectId} className="text-xs font-semibold font-body text-navy-700">
            {label}
          </label>
        )}
        <select
          ref={ref}
          id={selectId}
          className={cn(
            'input-base pr-8 appearance-none',
            'bg-[url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%23a0aec0\' d=\'M6 8L1 3h10z\'/%3E%3C/svg%3E")] bg-no-repeat bg-[right_0.75rem_center]',
            error && 'error',
            className
          )}
          {...props}
        >
          {options.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        {error && <p className="text-xs text-red-600 font-medium">{error}</p>}
      </div>
    );
  }
);
Select.displayName = 'Select';

// ── Textarea ──────────────────────────────────────────────────────────────────

interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, error, className, id, ...props }, ref) => {
    const textareaId = id ?? label?.toLowerCase().replace(/\s+/g, '-');
    return (
      <div className="flex flex-col gap-1.5">
        {label && (
          <label htmlFor={textareaId} className="text-xs font-semibold font-body text-navy-700">
            {label}
          </label>
        )}
        <textarea
          ref={ref}
          id={textareaId}
          className={cn(
            'input-base h-auto py-2.5 resize-none',
            error && 'error',
            className
          )}
          rows={3}
          {...props}
        />
        {error && <p className="text-xs text-red-600 font-medium">{error}</p>}
      </div>
    );
  }
);
Textarea.displayName = 'Textarea';

// ── Badge ─────────────────────────────────────────────────────────────────────

type BadgeVariant = 'default' | 'navy' | 'amber' | 'teal' | 'red' | 'green';

const badgeStyles: Record<BadgeVariant, string> = {
  default: 'bg-parchment-200 text-navy-700',
  navy:    'bg-navy-100 text-navy-800',
  amber:   'bg-amber-100 text-amber-800',
  teal:    'bg-teal-100 text-teal-800',
  red:     'bg-red-100 text-red-700',
  green:   'bg-green-100 text-green-700',
};

export function Badge({
  variant = 'default', children, className,
}: { variant?: BadgeVariant; children: React.ReactNode; className?: string }) {
  return (
    <span className={cn('badge', badgeStyles[variant], className)}>
      {children}
    </span>
  );
}

// ── Tooltip ───────────────────────────────────────────────────────────────────

export function Tooltip({ tip, children }: { tip: string; children: React.ReactNode }) {
  return (
    <span className="relative group inline-flex">
      {children}
      <span className="
        absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2.5 py-1.5
        bg-navy-900 text-white text-xs rounded-lg shadow-lg
        opacity-0 group-hover:opacity-100 pointer-events-none
        transition-opacity duration-150 z-50 whitespace-nowrap max-w-48
        text-center leading-tight
      ">
        {tip}
        <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-navy-900" />
      </span>
    </span>
  );
}

// ── Modal ─────────────────────────────────────────────────────────────────────

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  footer?: React.ReactNode;
}

const modalSizes = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-4xl' };

export function Modal({ open, onClose, title, description, children, size = 'md', footer }: ModalProps) {
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-navy-950/60 backdrop-blur-sm animate-fade-in" />

      {/* Dialog */}
      <div
        className={cn(
          'relative w-full bg-white rounded-xl2 shadow-card-lg',
          'flex flex-col max-h-[90vh] animate-slide-in',
          modalSizes[size]
        )}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-parchment-200">
          <div>
            <h2 className="font-display font-bold text-lg text-navy-900">{title}</h2>
            {description && <p className="text-sm text-navy-500 mt-0.5">{description}</p>}
          </div>
          <button
            onClick={onClose}
            className="ml-4 p-1.5 rounded-lg text-navy-400 hover:bg-parchment-100 hover:text-navy-700 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">{children}</div>

        {/* Footer */}
        {footer && (
          <div className="flex items-center justify-end gap-3 p-5 border-t border-parchment-200 bg-parchment-50">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Empty State ───────────────────────────────────────────────────────────────

export function EmptyState({
  icon, title, description, action,
}: {
  icon: React.ReactNode;
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-8 text-center gap-4 blueprint-bg rounded-xl border border-dashed border-parchment-300">
      <div className="w-16 h-16 rounded-2xl bg-parchment-100 flex items-center justify-center text-navy-400">
        {icon}
      </div>
      <div>
        <h3 className="font-display font-bold text-navy-700 text-lg">{title}</h3>
        {description && <p className="text-navy-400 text-sm mt-1 max-w-sm">{description}</p>}
      </div>
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

// ── Loading Spinner ───────────────────────────────────────────────────────────

export function Spinner({ size = 20, className }: { size?: number; className?: string }) {
  return (
    <svg
      className={cn('animate-spin text-navy-400', className)}
      width={size} height={size}
      viewBox="0 0 24 24" fill="none"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

// ── Section Label ─────────────────────────────────────────────────────────────

export function SectionLabel({ children }: { children: React.ReactNode }) {
  return <div className="section-divider">{children}</div>;
}

// ── Stat row ──────────────────────────────────────────────────────────────────

export function StatRow({ label, value, mono = true }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-parchment-100 last:border-b-0">
      <span className="text-xs text-navy-500 font-medium">{label}</span>
      <span className={cn('text-sm text-navy-900 font-semibold', mono && 'font-mono')}>{value}</span>
    </div>
  );
}
